import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { animations } from '../config/animations';
import { useContactForm } from '../hooks/useContactForm';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactModal = ({ isOpen, onClose }: ContactModalProps) => {
  const {
    formData,
    isSubmitting,
    isSubmitted,
    error,
    handleInputChange,
    handleSubmit,
    resetForm,
  } = useContactForm();

  const { t } = useTranslation();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]); // Remove resetForm from dependencies to prevent infinite loops

  // Auto-close after submission
  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isSubmitted]); // Remove onClose from dependencies to prevent infinite loops

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    await handleSubmit(e);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className='fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 sm:p-4 pt-16 sm:pt-4 bg-black/50 backdrop-blur-sm'
          onClick={handleClose}
        >
          <motion.div
            {...animations.scaleIn}
            className='relative w-full max-w-sm sm:max-w-md lg:max-w-2xl max-h-[75vh] sm:max-h-[90vh] bg-white rounded-2xl sm:rounded-3xl shadow-2xl flex flex-col'
            onClick={e => e.stopPropagation()}
          >
            {/* Header - Fixed */}
            <div className='flex-shrink-0 bg-white rounded-t-2xl sm:rounded-t-3xl px-4 sm:px-6 lg:px-8 py-3 sm:py-6 border-b border-gray-100'>
              <div className='flex items-center justify-between'>
                <h2 className='text-xl sm:text-2xl lg:text-3xl font-eras font-bold text-avoprime-primary'>
                  {t('contact.title')}
                </h2>
                <button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className='p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 disabled:opacity-50'
                >
                  <XMarkIcon className='w-6 h-6 text-gray-600' />
                </button>
              </div>
              <p className='text-sm sm:text-base text-gray-600 font-montserrat mt-2'>
                {t('contact.subtitle')}
              </p>
            </div>

            {/* Content */}
            <div className='flex-1 flex flex-col min-h-0 overflow-y-auto'>
              {isSubmitted ? (
                <div className='flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8'>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className='text-center py-6 sm:py-8 lg:py-12'
                  >
                    <CheckCircleIcon className='w-16 h-16 sm:w-20 sm:h-20 text-green-500 mx-auto mb-4 sm:mb-6' />
                    <h4 className='text-lg sm:text-xl lg:text-2xl font-eras font-bold text-avoprime-primary mb-3 sm:mb-4'>
                      {t('contact.form.success')}
                    </h4>
                    <p className='text-gray-600 font-montserrat'>
                      {t('contact.form.successMessage')}
                    </p>
                  </motion.div>
                </div>
              ) : (
                <>
                  {/* Form Content - Scrollable */}
                  <div className='flex-1 overflow-y-auto p-3 sm:p-6 lg:p-8'>
                    <form onSubmit={handleFormSubmit} className='space-y-4 sm:space-y-6'>
                      {/* Mensagem de erro */}
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className='bg-red-50 border border-red-200 rounded-lg p-4'
                        >
                          <p className='text-red-600 text-sm'>{error}</p>
                        </motion.div>
                      )}

                      <div className='grid md:grid-cols-2 gap-6'>
                        <div>
                          <label
                            htmlFor='modal-name'
                            className='block text-sm font-medium text-gray-700 mb-2'
                          >
                            {t('contact.form.name')} *
                          </label>
                          <input
                            type='text'
                            id='modal-name'
                            name='name'
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avoprime-primary focus:border-transparent transition-all duration-200'
                            placeholder={t('contact.form.placeholder.name')}
                          />
                        </div>

                        <div>
                          <label
                            htmlFor='modal-email'
                            className='block text-sm font-medium text-gray-700 mb-2'
                          >
                            {t('contact.form.email')} *
                          </label>
                          <input
                            type='email'
                            id='modal-email'
                            name='email'
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avoprime-primary focus:border-transparent transition-all duration-200'
                            placeholder={t('contact.form.placeholder.email')}
                          />
                        </div>
                      </div>

                      <div className='grid md:grid-cols-2 gap-6'>
                        <div>
                          <label
                            htmlFor='modal-phone'
                            className='block text-sm font-medium text-gray-700 mb-2'
                          >
                            {t('contact.form.phone')}
                          </label>
                          <input
                            type='tel'
                            id='modal-phone'
                            name='phone'
                            value={formData.phone}
                            onChange={handleInputChange}
                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avoprime-primary focus:border-transparent transition-all duration-200'
                            placeholder={t('contact.form.placeholder.phone')}
                          />
                        </div>

                        <div>
                          <label
                            htmlFor='modal-company'
                            className='block text-sm font-medium text-gray-700 mb-2'
                          >
                            {t('contact.form.company')}
                          </label>
                          <input
                            type='text'
                            id='modal-company'
                            name='company'
                            value={formData.company}
                            onChange={handleInputChange}
                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avoprime-primary focus:border-transparent transition-all duration-200'
                            placeholder={t('contact.form.placeholder.company')}
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor='modal-message'
                          className='block text-sm font-medium text-gray-700 mb-2'
                        >
                          {t('contact.form.message')} *
                        </label>
                        <textarea
                          id='modal-message'
                          name='message'
                          value={formData.message}
                          onChange={handleInputChange}
                          required
                          rows={4}
                          className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avoprime-primary focus:border-transparent transition-all duration-200 resize-none'
                          placeholder={t('contact.form.placeholder.message')}
                        />
                      </div>
                    </form>
                  </div>

                  {/* Buttons - Fixed at bottom */}
                  <div className='flex-shrink-0 p-4 sm:p-8 border-t border-gray-100 bg-white rounded-b-3xl'>
                    <div className='flex gap-4'>
                      <button
                        type='button'
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className='flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-montserrat font-medium rounded-full transition-all duration-200 hover:bg-gray-50 disabled:opacity-50'
                      >
                        {t('contact.form.cancel')}
                      </button>
                      <button
                        type='submit'
                        disabled={isSubmitting}
                        onClick={handleFormSubmit}
                        className='flex-1 bg-gradient-to-r from-avoprime-primary to-avoprime-secondary text-white font-eras text-lg px-6 py-3 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
                      >
                        {isSubmitting ? (
                          <div className='flex items-center justify-center space-x-2'>
                            <div className='w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
                            <span>{t('contact.form.sending')}</span>
                          </div>
                        ) : (
                          t('contact.form.submit')
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
